import React from 'react';

import Store from 'store';
import Session from 'store/session';
import { getProfilePath } from 'common';
import { presetItems } from 'logic/itemTypes';

const ImportContext = React.createContext();

function profileSnapshot(profile, state) {
  const snapshot = {
    initial: profile,
    imported: profile.importedId,
    skills: profile.skills,
    stats: profile.stats,
    class: profile.class,
    level: profile.level,
    merc: profile.merc,
    mercLevel: profile.mercLevel,
    quests: profile.quests,
    skillProgression: profile.skillProgression,

    items: {},
    inventory: [],
    cube: [],
    mercItems: {},
  };
  const item = id => state.items[id] || presetItems[id];
  if (profile.inventory) {
    snapshot.inventory = profile.inventory.map(item);
  }
  if (profile.cube) {
    snapshot.cube = profile.cube.map(item);
  }
  if (profile.items) for (const [slot, id] of Object.entries(profile.items)) {
    if (id) snapshot.items[slot] = item(id);
  }
  if (profile.mercItems) for (const [slot, id] of Object.entries(profile.mercItems)) {
    if (id) snapshot.mercItems[slot] = item(id);
  }
  if (profile.ironGolem) {
    snapshot.ironGolem = item(profile.ironGolem);
  }
  return snapshot;
}

function profileEquals(profile, snapshot, state) {
  if (profile.skills !== snapshot.skills) return false;
  if (profile.stats !== snapshot.stats) return false;
  if (profile.class !== snapshot.class) return false;
  if (profile.level !== snapshot.level) return false;
  if (profile.merc !== snapshot.merc) return false;
  if (profile.mercLevel !== snapshot.mercLevel) return false;
  if (profile.quests !== snapshot.quests) return false;
  if (profile.skillProgression !== snapshot.skillProgression) return false;

  const item = id => state.items[id] || presetItems[id];
  function equalItems(lhs, rhs) {
    if (!lhs || !rhs) return !lhs === !rhs;
    const key1 = Object.keys(lhs).filter(key => lhs[key]);
    const key2 = Object.keys(rhs).filter(key => rhs[key]);
    const keys = new Set([...key1, ...key2]);
    for (const key of keys) {
      if (item(lhs[key]) !== rhs[key]) return false;
    }
    return true;
  }

  if (!equalItems(profile.items, snapshot.items)) return false;
  if (!equalItems(profile.mercItems, snapshot.mercItems)) return false;
  if (!equalItems(profile.inventory, snapshot.inventory)) return false;
  if (!equalItems(profile.cube, snapshot.cube)) return false;

  if (profile.ironGolem || snapshot.ironGolem) {
    if (item(profile.ironGolem) !== snapshot.ironGolem) return false;
  }

  return true;
}

function profileSnapshotLite(profile, state) {
  return {
    imported: profile.importedId,
    skills: profile.skills,
    stats: profile.stats,
    class: profile.class,
    level: profile.level,
    merc: profile.merc,
    mercLevel: profile.mercLevel,
    quests: profile.quests,
    skillProgression: profile.skillProgression,

    items: profile.items,
    inventory: profile.inventory,
    cube: profile.cube,
    mercItems: profile.mercItems,
  };
}

function profileEqualsLite(profile, snapshot, state) {
  if (profile.skills !== snapshot.skills) return false;
  if (profile.stats !== snapshot.stats) return false;
  if (profile.class !== snapshot.class) return false;
  if (profile.level !== snapshot.level) return false;
  if (profile.merc !== snapshot.merc) return false;
  if (profile.mercLevel !== snapshot.mercLevel) return false;
  if (profile.quests !== snapshot.quests) return false;
  if (profile.skillProgression !== snapshot.skillProgression) return false;
  if (profile.items !== snapshot.items) return false;
  if (profile.mercItems !== snapshot.mercItems) return false;
  if (profile.inventory !== snapshot.inventory) return false;
  if (profile.cube !== snapshot.cube) return false;
  return true;
}

function stateSnapshot(state) {
  return {
    initial: state,
    imported: state.importedId,
    profiles: state.profiles.map(profile => profileSnapshotLite(profile, state)),
    items: state.items,
  };
}
function stateEquals(state, snapshot) {
  if (state.items !== snapshot.items) return false;
  if (state.profiles.length !== snapshot.profiles?.length) return false;
  return state.profiles.every((profile, index) => profileEqualsLite(profile, snapshot.profiles[index], state));
}

export function useImportId() {
  return React.useContext(ImportContext);
}

export function ImportTracker({ children }) {
  const [imported, setImported] = React.useState();

  const store = Store.useStore();
  const dispatch = Store.useDispatch();

  const userId = Session.useSelector(state => state.userId);

  const importedRef = React.useRef();
  const snapshotRef = React.useRef({});

  React.useEffect(() => {
    function update() {
      const state = store.getState();

      const snapshots = snapshotRef.current;
      const newSnapshots = snapshotRef.current = {};
  
      let demoteState = false, modifiedState = false;
      if (state.imported) {
        if (!snapshots[state.id] || snapshots[state.id].imported !== state.importedId) {
          newSnapshots[state.id] = stateSnapshot(state);
        } else if (stateEquals(state, snapshots[state.id])) {
          newSnapshots[state.id] = snapshots[state.id];
          if (state.imported !== 'modified' && state !== snapshots[state.id].initial) {
            if (snapshots[state.id].initial) {
              modifiedState = true;
              snapshots[state.id].initial = null;
            } else {
              snapshots[state.id].initial = state;
            }
          }
        } else {
          demoteState = true;
        }
      }
  
      const demoteIds = new Set(), modifiedIds = new Set();
      for (const profile of state.profiles) {
        if (profile.imported) {
          if (!snapshots[profile.id] || snapshots[profile.id].imported !== profile.importedId) {
            newSnapshots[profile.id] = profileSnapshot(profile, state);
          } else if (profileEquals(profile, snapshots[profile.id], state)) {
            newSnapshots[profile.id] = snapshots[profile.id];
            if (profile.imported !== 'modified' && profile !== snapshots[profile.id].initial) {
              if (snapshots[profile.id].initial) {
                modifiedIds.add(profile.id);
                snapshots[profile.id].initial = null;
              } else {
                snapshots[profile.id].initial = profile;
              }
            }
          } else {
            demoteIds.add(profile.id);
          }
        }
      }
  
      function updateUrl(data, hash) {
        const id = (data?.imported && data.importedId) || null;
        if (importedRef.current !== id) {
          importedRef.current = id;
          setImported(id);
        }
  
        const url = new URL(getProfilePath(id || ''), window.location);
        if (window.location.search) url.search = window.location.search;
        if (!id && data?.importedId) {
          url.hash = '#' + data.importedId;
        } else if (hash) {
          url.hash = hash;
        }
        if (url.href !== window.location.href) {
          window.history?.replaceState(null, '', url.href);
        }
      }
      
      const active = state.profiles[state.activeProfile];
      if (active.imported && !demoteIds.has(active.id)) {
        updateUrl(active);
      } else if (state.imported && !demoteState) {
        updateUrl(state, state.profiles.length > 1 && `#${state.activeProfile + 1}`);
      } else if (active.importedId && userId && active.user === userId) {
        updateUrl(active);
      } else if (state.importedId && userId && state.user === userId) {
        updateUrl(state);
      } else {
        updateUrl();
      }
  
      if (demoteState || demoteIds.size || modifiedState || modifiedIds.size) {
        dispatch.setState(state => {
          state = {...state};
          if (demoteState) state.imported = false;
          else if (modifiedState) state.imported = 'modified';
          if (demoteIds.size || modifiedIds.size) {
            state.profiles = state.profiles.map(profile => {
              if (demoteIds.has(profile.id)) {
                profile = {...profile, imported: false};
              } else if (modifiedIds.has(profile.id)) {
                profile = {...profile, imported: 'modified'};
              }
              return profile;
            });
          }
          return state;
        });
      }
    }
    update();
    return store.subscribe(update);
  }, [store, dispatch, userId]);

  return (
    <ImportContext.Provider value={imported}>
      {children}
    </ImportContext.Provider>
  );
}
